import { css } from '@emotion/react';
import {
  faCog, faFillDrip, faPen, faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { animated } from '@react-spring/web';
import {
  type FC, type PropsWithChildren, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons/roundButton/roundButton.component';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { RadioComponent } from '~/_shared/baseComponents/radio/radio.component';
import { useHighlightAnimation } from '~/_shared/hooks/useHighlightAnimation';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { boundaryTerritoryGroupInSidebarHighlighted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import { type ColorResult } from '../../../../../_shared/components/colorPicker/colorPicker.types';
import { type Theme } from '../../../../../_shared/themes/theme.model';
import { useTranslation } from '../../../../../_shared/utils/hooks';
import { ActiveBoundaryHeaderComponent } from './activeBoundaryHeader.component';
import { ActiveBoundaryContextMenuComponent } from './contextMenu/activeBoundaryContextMenu.component';
import {
  actionsWrapperStyle, boundaryOptionsHeaderStyle, boundaryOptionStyle, checkboxStyle, primaryBoundaryLabelStyle,
  toolbarButtonStyle, toolbarButtonWrapperStyle,
} from './styles';

export type BoundaryTerritoryKeySettings = Readonly<{
  isCustom: boolean;
  isEmpty: boolean;
  isVisible: boolean;
  label: string;
  selectedColor: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;

  onBatchEditLocationsClick?: () => void;
  onDelete?: () => void;
  onExportData?: () => void;
  onExportContainedBoundaries?: () => void;
  onToggle: () => void;
}>;

export type BoundaryKeySettings = Readonly<{
  isCustom: boolean;
  isVisible: boolean;
  label: string;
  selectedColor: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;

  onBatchEditLocationsClick?: () => void;
  onBoundarySettingsClick?: () => void;
  onExportDataInBoundaryClick?: () => void;
  onExportContainedBoundaries?: () => void;
  onManuallyEditPolygonClick?: () => void;
  onRemoveBoundaryClick?: () => void;
  onToggle: () => void;
  onZoomToBoundaryClick: () => void;
}>;

export type ActiveBoundaryProps = Readonly<{
  activeExtraBoundaryFillId?: Uuid | null;
  boundariesAsTerritories: boolean;
  boundaryGroupId: number;
  boundaryIsOptimizedTerritoriesTrial: boolean;
  boundaryTerritoryType: BoundaryTerritoryType;
  className?: string;
  fadeWhenInactive: boolean;
  hideLabels: boolean;
  hideWithoutData: boolean;
  ignoreFilters: boolean;
  isAiGenerated: boolean;
  isHighlighted?: boolean;
  isPrimary: boolean;
  isSubheaderHidden?: boolean;
  legendMode?: boolean;
  name: string;
  showToolbar: boolean;
  boundaryPermissions: BoundaryGroupPermissions;

  onClone?: () => void;
  onConvert?: () => void;
  onCustomizeFillClick: () => void;
  onCustomizeMetricsClick: () => void;
  onExportClick?: () => void;
  onRemove: () => void;
  onSettingsClick: () => void;
  onStartEditClick?: () => void;
  onToggleBoundariesAsTerritories?: () => void;
  onToggleFadeWhenInactive?: () => void;
  onToggleHideLabels?: () => void;
  onToggleHideWithoutData?: () => void;
  onToggleIgnoreFilters?: () => void;
  onToggleIsPrimary: () => void;
}>;

const wmsTrialStyle = (theme: Theme) => css({
  border: `2px solid ${theme.textColors.warning}`,
  borderRightWidth: '3px', // scrollbar eats 1px
});

export const ActiveBoundaryComponent: FC<PropsWithChildren<ActiveBoundaryProps>> = ({
  boundariesAsTerritories,
  boundaryGroupId,
  boundaryIsOptimizedTerritoriesTrial,
  boundaryTerritoryType,
  children,
  className,
  fadeWhenInactive,
  hideLabels,
  hideWithoutData,
  ignoreFilters,
  isAiGenerated,
  isHighlighted,
  isPrimary,
  isSubheaderHidden,
  legendMode,
  name,
  boundaryPermissions,
  onClone,
  onConvert,
  onCustomizeFillClick,
  onCustomizeMetricsClick,
  onExportClick,
  onRemove,
  onSettingsClick,
  onStartEditClick,
  onToggleBoundariesAsTerritories,
  onToggleFadeWhenInactive,
  onToggleHideLabels,
  onToggleHideWithoutData,
  onToggleIgnoreFilters,
  onToggleIsPrimary,
  showToolbar,
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { boundaryIsCustom } = boundaryPermissions;

  const { startHighlightAnimation, dynamicStyles, staticHighlightStyles, isAnimationActive } = useHighlightAnimation();

  const highlightCallback = useCallback((node: HTMLDivElement) => {
    if (isHighlighted && node) {
      if (!isAnimationActive) {
        node.scrollIntoView();
        startHighlightAnimation();
      }
      dispatch(boundaryTerritoryGroupInSidebarHighlighted());
    }
  }, [isHighlighted, isAnimationActive, dispatch, startHighlightAnimation]);

  const getCustomizeFillButtonStyle = () => {
    if (boundaryIsOptimizedTerritoriesTrial) {
      return RoundButtonStyle.Restricted;
    }
    else if (boundaryTerritoryType === BoundaryTerritoryType.Manual) {
      return RoundButtonStyle.Primary;
    }
    else {
      return RoundButtonStyle.Quaternary;
    }
  };

  const getEditButtonStyle = () => {
    if (!onStartEditClick) {
      return RoundButtonStyle.Disabled;
    }

    if (boundaryIsOptimizedTerritoriesTrial) {
      return RoundButtonStyle.Restricted;
    }

    return RoundButtonStyle.Primary;
  };

  const getEditButtonTooltip = () => {
    if (!onStartEditClick) {
      if (boundaryIsCustom && !boundaryPermissions.boundaryIsEditable) {
        return t('Only the owner ({{ownerName}}) is allowed to edit this territory group',
          { ownerName: boundaryPermissions.boundaryOwnerName });
      }

      return t('You cannot edit territories when an alternative fill is active');
    }

    if (boundaryIsCustom) {
      return t('Start Edit Mode');
    }

    return t('Start Selection Mode');
  };

  return (
    <animated.div
      style={dynamicStyles}
      ref={highlightCallback}
      css={[staticHighlightStyles, boundaryIsOptimizedTerritoriesTrial && wmsTrialStyle]}
      className={className}
    >
      <ActiveBoundaryHeaderComponent
        isAiGenerated={isAiGenerated}
        boundaryPermissions={boundaryPermissions}
        name={name}
      />

      {!legendMode && (
        <div css={actionsWrapperStyle}>
          <div>
            <RadioComponent
              isChecked={isPrimary}
              onChange={onToggleIsPrimary}
              label={t('Active')}
              css={primaryBoundaryLabelStyle}
              value="active"
            />
          </div>

          <div>
            <div css={toolbarButtonWrapperStyle}>
              {showToolbar && (
                <>
                  <div css={toolbarButtonStyle}>
                    <RoundButtonComponent
                      isDisabled={!onStartEditClick}
                      buttonStyle={getEditButtonStyle()}
                      icon={faPen}
                      onClick={onStartEditClick}
                      size={RoundButtonSize.Small}
                      tooltipLabel={getEditButtonTooltip()}
                    />
                  </div>

                  <div css={toolbarButtonStyle}>
                    <RoundButtonComponent
                      icon={faFillDrip}
                      onClick={onCustomizeFillClick}
                      size={RoundButtonSize.Small}
                      tooltipLabel={t('Customize Fill by data')}
                      buttonStyle={getCustomizeFillButtonStyle()}
                    />
                  </div>

                  <div css={toolbarButtonStyle}>
                    <RoundButtonComponent
                      icon={faCog}
                      onClick={onSettingsClick}
                      size={RoundButtonSize.Small}
                      tooltipLabel={t(boundaryIsCustom ? 'Territory Group Settings' : 'Boundary Settings')}
                    />
                  </div>

                  <div css={toolbarButtonStyle}>
                    <RoundButtonComponent
                      icon={faXmark}
                      onClick={onRemove}
                      buttonStyle={RoundButtonStyle.Danger}
                      size={RoundButtonSize.Small}
                      tooltipLabel={t(boundaryIsCustom ?
                        'Remove Territory Group from Map' :
                        'Remove Boundary from Map')}
                    />
                  </div>

                  <ActiveBoundaryContextMenuComponent
                    boundaryGroupId={boundaryGroupId}
                    isBoundaryGroupCustom={boundaryIsCustom}
                    onConvert={onConvert}
                    onClone={onClone}
                    onCustomizeMetricsClick={onCustomizeMetricsClick}
                    onExportClick={onExportClick}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {!legendMode && !isSubheaderHidden && (
        <div css={boundaryOptionsHeaderStyle}>
          {onToggleHideLabels && (
            <div css={boundaryOptionStyle}>
              <CheckboxComponent
                isChecked={hideLabels}
                checkedSetter={onToggleHideLabels}
                text={t('Hide Labels')}
                textStyle={checkboxStyle}
              />
            </div>
          )}

          {onToggleIgnoreFilters && (
            <div css={boundaryOptionStyle}>
              <CheckboxComponent
                isChecked={ignoreFilters}
                checkedSetter={onToggleIgnoreFilters}
                text={t('Ignore Filters')}
                textStyle={checkboxStyle}
              />
            </div>
          )}

          {onToggleBoundariesAsTerritories && (
            <div css={boundaryOptionStyle}>
              <CheckboxComponent
                isChecked={boundariesAsTerritories}
                checkedSetter={onToggleBoundariesAsTerritories}
                text={t('Combine Groups')}
                textStyle={checkboxStyle}
              />
            </div>
          )}

          {onToggleHideWithoutData && (
            <div css={boundaryOptionStyle}>
              <CheckboxComponent
                isChecked={hideWithoutData}
                checkedSetter={onToggleHideWithoutData}
                text={t('Hide Areas w/o Data')}
                textStyle={checkboxStyle}
              />
            </div>
          )}

          {onToggleFadeWhenInactive && (
            <div css={boundaryOptionStyle}>
              <CheckboxComponent
                isChecked={fadeWhenInactive}
                checkedSetter={onToggleFadeWhenInactive}
                text={t('Fade When Inactive')}
                textStyle={checkboxStyle}
              />
            </div>
          )}
        </div>
      )}
      {children}
    </animated.div>
  );
};
